import { NewgenusTimestamp } from './timestamp';

export interface SecurityTask {
    partKey?: string;
    policyId?: string;
    featureId?: string;
    hatId?: string;
    securityGroupId?: string;
    uid?: string;

    /**
     * Blame tracking to know who did what to the original document and when.
     * `null` (not `undefined`) when neither side of the write exists — the
     * admin serializer rejects `undefined` values (see backend `taskBlame.ts`).
     */
    blameDetails?: {
        createdBy: string;
        createdOn: NewgenusTimestamp;
        modifiedBy: string;
        modifiedOn: NewgenusTimestamp;
    } | null;

    orgId: string;
    snapshotNew: any;
    snapshotOld: any;
    diffNew: any;
    diffOld: any;
    updateTimeStamp: NewgenusTimestamp;
    createdTimeStamp: NewgenusTimestamp;
    completedTimestamp?: NewgenusTimestamp;
    typeOfTask: SecurityTaskOperations;
    taskNumber: number;
    status: 'ready' | 'processing' | 'Error' | 'error' | 'completed' | 'failed' | 'fulfilled' | 'queued' | '';
    run: number;
    key: string;
    isThrowError: boolean;
    error?: string;
    errorCode?: string;
    description?: string;

    /**
     * Sequence number for task ordering within a conflict group.
     * Used to ensure tasks are processed in the correct order.
     */
    sequence?: number;

    /**
     * Conflict key for grouping related tasks that must be processed sequentially.
     * Format: "org:{orgId}:part:{partKey}" or similar.
     */
    conflictKey?: string;
}

export enum SecurityTaskOperations {
    updateSecurityGroup = 'updateSecurityGroup',
    addSecurityGroup = 'addSecurityGroup',
    removeSecurityGroup = 'removeSecurityGroup',
    addFeature = 'addFeature',
    removeFeature = 'removeFeature',
    addFeaturePermissions = 'addFeaturePermissions',
    removeFeaturePermissions = 'removeFeaturePermissions',
    addPolicy = 'addPolicy',
    removePolicy = 'removePolicy',
    updatePolicy = 'updatePolicy',
    addHat = 'addHat',
    removeHat = 'removeHat',
    updateHat = 'updateHat',
    addPart = 'addPart',
    removePart = 'removePart',
    updatePart = 'updatePart',
    reorderChildren = 'reorderChildren',
    bulkAddParts = 'bulkAddParts',
    bulkDeleteParts = 'bulkDeleteParts',
    bulkSave = 'bulkSave',
    updateUser = 'updateUser',
    addUser = 'addUser',
    removeUser = 'removeUser',
}

/**
 * A node in the bulk position tree as constructed by the frontend.
 * Backend will flatten this into real OrgPart documents.
 */
export interface BulkPartNode {
    /** Client-generated temporary ID for tracking within the builder */
    tempId: string;
    /** Display name for the position */
    name: string;
    /** Part type: executive, division, department, section, unit, post */
    partType: string;
    /** Colour settings — inherited from parent by default, user can override */
    colour: {
        color: string;
        background: string;
        linkColor: string;
    };
    /** Optional VFP (Valuable Final Product) description */
    vfp?: string;
    /**
     * Optional role within the parent container's header strip.
     * Only meaningful for `partType === 'post'`.
     * See {@link OrgPart.headerRole} for full semantics.
     */
    headerRole?: 'head' | 'deputy' | 'assistant';
    /**
     * Optional detached post group (e.g. 'board' = the Board of Directors box).
     * Only meaningful for `partType === 'post'`.
     * See {@link OrgPart.detachedGroup} for full semantics.
     */
    detachedGroup?: string;
    /** partKey of the division the detached box draws its cosmetic connector to. */
    detachedGroupConnectsTo?: string;
    /** True = the post does not cascade Held-From-Above (governance seats). */
    excludeFromHfa?: boolean;
    /** partKey of the post whose holders this post shows (linked post). */
    linkedPartKey?: string;
    /** Child nodes — recursive tree structure */
    children: BulkPartNode[];
}

/**
 * A graft point represents new nodes to be added as children of an
 * existing part that is already visible in the bulk builder.
 * All graft points are included in a single bulkAddParts command
 * and processed atomically to avoid duplicate orgParts entries.
 */
export interface BulkGraftPoint {
    /** The real partKey of the existing parent node */
    existingPartKey: string;
    /** New child subtrees to attach under that parent */
    children: BulkPartNode[];
    /** Ordered keys of ALL children under this parent (existing + new) for reindexing */
    childOrder: string[];
}

/**
 * Command document written to Firestore to trigger bulk part creation.
 * Written to organizations/{orgKey}/parts/{commandDocId}.
 * Backend detects action: 'bulkAddParts' and processes atomically.
 */
export interface BulkAddPartsCommand {
    /** The document key (same as commandDocId) */
    partKey: string;
    /** Action identifier for the writeOrgPart trigger */
    action: 'bulkAddParts';
    /**
     * The existing part that the new tree attaches to.
     * - For 'child': the parent under which new children are added
     * - For 'sibling': the part whose siblings list we're adding to
     */
    anchorPartKey: string;
    /** Whether we're adding children under anchorPart or siblings next to it */
    insertionType: 'child' | 'sibling';
    /** The tree of new positions to create */
    tree: BulkPartNode[];
    /** Total count of positions in the tree (for validation) */
    totalCount: number;
    /**
     * Optional ordered list of all root-level sibling keys (existing + new)
     * in their desired order.  Existing parts use their real partKey,
     * new parts use their tempId.  The backend uses this to reindex the
     * path positions of all siblings under the parent.
     * Omitted when there are no existing siblings to interleave with.
     */
    siblingOrder?: string[];
    /**
     * Graft points: new nodes to be added under existing parts within the tree.
     * Each graft point specifies an existing parent and the new children to add.
     * Processed atomically in the same batch as the root tree to prevent duplicates.
     */
    graftPoints?: BulkGraftPoint[];
    /** Who requested this operation */
    requestedBy: string;
    /** When it was requested */
    requestedAt: any; // FirestoreTimestamp
}

/**
 * Command document written to Firestore to trigger bulk part deletion.
 * Written to organizations/{orgKey}/parts/{commandDocId}.
 * Backend detects action: 'bulkDeleteParts' and processes atomically.
 *
 * Deletes the specified parts AND all their descendants, reindexes remaining
 * siblings, and cleans up hat references — all in a single transaction.
 */
export interface BulkDeletePartsCommand {
    /** The document key (same as commandDocId) */
    partKey: string;
    /** Action identifier for the writeOrgPart trigger */
    action: 'bulkDeleteParts';
    /** Array of partKeys to delete (descendants are auto-included) */
    partKeysToDelete: string[];
    /** Total count including descendants (for logging, calculated by frontend) */
    totalCount: number;
    /** Who requested this operation */
    requestedBy: string;
    /** When it was requested */
    requestedAt: any; // FirestoreTimestamp
}

/**
 * A single field-level update to apply to an existing OrgPart.
 * Only the listed fields will be merged; everything else is preserved.
 */
export interface BulkSaveUpdate {
    /** The real partKey of the existing part to update */
    partKey: string;
    /** Partial fields to merge into the part (e.g. name, vfp, colour, headerRole, partType) */
    fields: Record<string, any>;
}

/**
 * Reorder operation embedded inside a bulkSave command.
 * Same shape that reorderChildren uses, but bundled instead of as a standalone task.
 */
export interface BulkSaveReorder {
    /** The parent whose children are being reordered ('root' for top-level) */
    parentPartKey: string;
    /** Full ordered list of child partKeys after reorder */
    newChildOrder: string[];
}

/**
 * Move an existing part (with its descendants) to a new parent.
 *
 * The handler validates:
 *  - `partKey` exists in orgParts
 *  - `newParentPartKey` exists in orgParts
 *  - `newParentPartKey` is NOT `partKey` itself
 *  - `newParentPartKey` is NOT a descendant of `partKey` (no cycles)
 *  - `newParentPartKey` is a container type (not a 'post' leaf), unless we explicitly allow nested posts
 *
 * After all moves are applied in-memory, the bulkSave handler recomputes
 * `path` / `parentRef` / `depth` for every affected subtree, reindexes the
 * old parent's remaining children, and queues per-part subcollection updates
 * into the same atomic batch as the rest of the save.
 */
export interface BulkSaveMove {
    /** The part to move (its descendants come along automatically) */
    partKey: string;
    /** The new parent partKey */
    newParentPartKey: string;
    /**
     * Optional zero-based index into the new parent's child list. If omitted
     * (or out of range), the moved subtree is appended to the end.
     */
    newIndex?: number;
}

/**
 * Add-bundle embedded inside a bulkSave command.
 * Mirrors the meaningful payload of BulkAddPartsCommand.
 */
export interface BulkSaveAdd {
    anchorPartKey: string;
    insertionType: 'child' | 'sibling';
    tree: BulkPartNode[];
    totalCount: number;
    siblingOrder?: string[];
    graftPoints?: BulkGraftPoint[];
}

/**
 * Atomic command document that bundles updates, an add-bundle, reorders and deletes
 * into a SINGLE backend task with a SINGLE sequence number.
 *
 * Eliminates the trigger-fire race that occurred when the bulk-builder fanned-out
 * N updatePart writes + 1 bulkAddParts + M reorderChildren + 1 bulkDeleteParts as
 * separate command/subcollection writes — each of which previously raced for its
 * own sequence number at trigger-fire time.
 *
 * Written to organizations/{orgKey}/parts/{commandDocId}.
 * Backend detects action: 'bulkSave' and creates ONE SecurityTask with
 * conflictKey: 'orgParts:{orgKey}', so the V3 processor runs the whole save
 * strictly serial against any other orgParts work for this org.
 */
export interface BulkSaveCommand {
    /** The document key (same as commandDocId) */
    partKey: string;
    /** Action identifier for the writeOrgPart trigger */
    action: 'bulkSave';
    /** Field-level updates to existing parts (Phase 1 inside the handler) */
    updates: BulkSaveUpdate[];
    /** Optional add-bundle: tree of new positions to create (Phase 2 inside the handler) */
    add: BulkSaveAdd | null;
    /**
     * Cross-parent moves (Phase M inside the handler — runs AFTER updates and BEFORE adds).
     * Each entry detaches a subtree from its current parent and reattaches it under a
     * new parent at an optional index. All paths/depths are recomputed atomically.
     *
     * Optional for backward compatibility — older command docs may omit this field.
     */
    moves?: BulkSaveMove[];
    /** Reorders to apply (Phase 3 inside the handler) */
    reorders: BulkSaveReorder[];
    /** PartKeys to delete — descendants are auto-included (Phase 4 inside the handler) */
    deletes: string[];
    /** Who requested this operation */
    requestedBy: string;
    /** When it was requested */
    requestedAt: any; // FirestoreTimestamp
}
